<script setup>
import { computed } from 'vue';

const props = defineProps({
    /**
      * The `items` prop should be an array of breadcrumb items. Each item
      * can either be a string, for a breadcrumb item without a link; or an
      * array of length two where the first item is the label and the second item
      * is the URL. If the URL is a function it is bound to the click event
      * on the link. If the URL is an object it is passed as the `to` prop to a
      * RouterLink component (from vue-router).
      */
    items: {
        type: Array,
        default: () => [],
    },
});

const breadcrumbs = computed(() => {
    const res = [];
    for (let item of props.items) {
        const [label, url] = Array.isArray(item) ? item : [item, null];
        const breadcrumb = {
            label,
        };
        if (url) {
            if (typeof url === "object") {
                breadcrumb.router_link = url;
            } else if (typeof url === "function") {
                breadcrumb.link = {
                    attrs: { href: '#' },
                    on: { click: (event) => {
                        event.preventDefault();
                        url(event);
                    } },
                };
            } else {
                breadcrumb.link = {
                    attrs: { href: url },
                    on: {},
                };
            }
        }
        res.push(breadcrumb);
    }
    if (res.length) {
        res[res.length - 1].last = true;
    }
    return res;
});

</script>

<template>
    <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
            <li
                v-for="(item, index) in breadcrumbs"
                :key="index"
                :class="['breadcrumb-item', { 'breadcrumb-item-active': item.last }]"
                :aria-current="item.last ? 'page' : null"
            >
                <a v-if="item.link" v-bind="item.link.attrs" v-on="item.link.on">
                    {{ item.label }}
                </a>
                <!-- We use <component> instead of the <RouterLink> to avoid
                     having to depend on the component when not in a context that uses vue-router -->
                <component :is="'RouterLink'" v-else-if="item.router_link" :to="item.router_link">
                    {{ item.label }}
                </component>
                <template v-else>
                    {{ item.label }}
                </template>
            </li>
        </ol>
    </nav>
</template>
